
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface ServiceSubscribeCardProps {
  productId: string;
  title: string;
  description: string | React.ReactNode;
  price?: number;
  customPrice?: string;
  weeklyPrice?: string;
  features: string[];
  note?: string | React.ReactNode;
  ctaText?: string;
  isPrimary?: boolean;
  className?: string;
  isSubscription?: boolean;
  stripeProductId?: string;
  link?: string;
  showContactInfo?: boolean;
}

const ServiceSubscribeCard: React.FC<ServiceSubscribeCardProps> = ({
  productId,
  title,
  description,
  price,
  customPrice,
  weeklyPrice,
  features,
  note,
  ctaText = 'Subscribe Now',
  isPrimary = false,
  className,
  isSubscription = true,
  stripeProductId,
  link,
  showContactInfo = false
}) => {
  const navigate = useNavigate();

  const handleSubscribe = () => {
    navigate('/contact');
  };
  

  return (
    <Card className={cn(
      "h-full transition-all hover:shadow-lg relative flex flex-col border-2 border-brand-teal shadow-md",
      className
    )}>
      <CardHeader>
        <CardTitle className="text-xl font-bold">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="mb-4">
          {customPrice ? (
            <span className="text-2xl font-bold">{customPrice}</span>
          ) : (
            <span className="text-2xl font-bold">${price}{isSubscription ? '/month' : ''}</span>
          )}
          {weeklyPrice && (
            <span className="text-sm text-gray-500 ml-2">({weeklyPrice})</span>
          )}
        </div>
        <div className="mb-2">
          <p className="text-sm font-semibold text-gray-700 mb-2">Includes:</p>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <Check className="h-5 w-5 text-brand-teal flex-shrink-0 mr-2 mt-0.5" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        {note && (
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600"><span className="font-semibold">Note:</span> {note}</p>
          </div>
        )}
        {link && (
          <div className="mt-4">
            <a 
              href={link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-brand-teal hover:underline text-sm font-medium inline-flex items-center"
            >
              Learn more <ArrowRight className="ml-1 h-4 w-4" />
            </a>
          </div>
        )}
      </CardContent>
      <CardFooter className="mt-auto">
        {showContactInfo ? (
          <div className="w-full bg-gray-50 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold text-gray-900 text-center mb-3">Contact Us</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-center gap-2">
                <span className="font-medium text-gray-700">Phone:</span>
                <span className="text-gray-900">+64 212 763 763</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <span className="font-medium text-gray-700">Email:</span>
                <span className="text-gray-900">connect@thinktomorrow.ai</span>
              </div>
            </div>
          </div>
        ) : (
          <Button 
            onClick={handleSubscribe}
            variant={isPrimary ? "primary" : "outline"} 
            className={cn(
              "w-full",
              isPrimary && "bg-gradient-to-r from-brand-teal to-brand-purple"
            )}
          >
            {ctaText}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default ServiceSubscribeCard;
